import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Filter, Plus } from "lucide-react";

import { ApiError, formatCLP, formatDate, formatDateTime } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  Cargando,
  cn,
  controlClasses,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  listarPendientesEmpresa,
  listarPorSocio,
  registrarMulta,
  type EstadoMulta,
  type MultaCreatePayload,
  type OrigenMulta,
} from "./api";
import { fetchContratos, type ContratoOperativo } from "../socios/api";

const ORIGENES: OrigenMulta[] = ["Manual", "Reunion", "Corte"];

const ESTADO_TONE: Record<EstadoMulta, BadgeTone> = {
  Pendiente: "amber",
  Facturada: "brand",
  Pagada: "emerald",
  Anulada: "slate",
};

const ORIGEN_TONE: Record<OrigenMulta, BadgeTone> = {
  Manual: "slate",
  Reunion: "brand",
  Corte: "rose",
};

export function MultasListPage(): JSX.Element {
  const [socioFiltro, setSocioFiltro] = useState<string>("");
  const [openForm, setOpenForm] = useState(false);

  const contratosQ = useQuery({
    queryKey: ["contratos"],
    queryFn: fetchContratos,
  });

  // Si no hay socio seleccionado mostramos las pendientes de toda la empresa.
  // Si hay socio, mostramos TODAS sus multas (no solo pendientes) para que
  // la directiva vea el histórico completo.
  const multasQ = useQuery({
    queryKey: ["multas", socioFiltro || "todas-pendientes"],
    queryFn: () =>
      socioFiltro
        ? listarPorSocio(Number(socioFiltro), false)
        : listarPendientesEmpresa(),
  });

  // Mapa socio_id → nombre para enriquecer la tabla.
  const socioById = useMemo(() => {
    const m = new Map<number, ContratoOperativo>();
    for (const c of contratosQ.data ?? []) m.set(c.socio_id, c);
    return m;
  }, [contratosQ.data]);

  const totalPendiente = (multasQ.data ?? [])
    .filter((m) => m.estado === "Pendiente")
    .reduce((acc, m) => acc + m.monto, 0);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Multas"
        icon={AlertTriangle}
        subtitle={
          multasQ.data
            ? `${multasQ.data.length} ${multasQ.data.length === 1 ? "multa" : "multas"}`
            : undefined
        }
        actions={
          <Button icon={Plus} onClick={() => setOpenForm((v) => !v)}>
            Registrar multa
          </Button>
        }
      />

      {openForm && (
        <RegistrarMultaForm
          contratos={contratosQ.data ?? []}
          onClose={() => setOpenForm(false)}
        />
      )}

      <Card padding="sm">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Filter className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />
          <select
            value={socioFiltro}
            onChange={(e) => setSocioFiltro(e.target.value)}
            aria-label="Filtrar multas por socio"
            className={cn(controlClasses, "w-auto")}
          >
            <option value="">Pendientes de toda la empresa</option>
            {(contratosQ.data ?? []).map((c) => (
              <option key={c.id} value={c.socio_id}>
                {c.socio_nombre} ({c.socio_rut})
              </option>
            ))}
          </select>
          <span className="ml-auto text-sm font-medium text-neutral-700 tabular-nums">
            Total pendiente: {formatCLP(totalPendiente)}
          </span>
        </div>
      </Card>

      {multasQ.isPending && <Cargando />}
      {multasQ.isError && (
        <ErrorCarga
          error={multasQ.error}
          mensaje="Error al cargar multas."
          onReintentar={() => void multasQ.refetch()}
        />
      )}

      {multasQ.data && multasQ.data.length === 0 && (
        <EmptyState
          icon={CheckCircle2}
          tone="emerald"
          title="Sin multas pendientes"
          description="Todos los socios están al día con sus obligaciones. Cuando se generen multas (manuales, por inasistencia a reuniones o por corte), aparecerán acá."
        />
      )}

      {multasQ.data && multasQ.data.length > 0 && (
        <TableWrap label="Multas">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Generada</th>
                <th scope="col" className="px-3 py-2 text-left">Socio</th>
                <th scope="col" className="px-3 py-2 text-left">Origen</th>
                <th scope="col" className="px-3 py-2 text-left">Motivo</th>
                <th scope="col" className="px-3 py-2 text-right">Monto</th>
                <th scope="col" className="px-3 py-2 text-left">Vencimiento</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-left">Boleta</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {multasQ.data.map((m) => {
                const c = socioById.get(m.socio_id);
                return (
                  <tr key={m.id} className="hover:bg-neutral-50">
                    <td className="px-3 py-2 text-neutral-600">
                      {formatDateTime(m.fecha_generacion)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-neutral-900">
                        {c?.socio_nombre ?? `socio #${m.socio_id}`}
                      </div>
                      <div className="text-xs text-neutral-500">
                        {c?.socio_rut ?? ""}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={ORIGEN_TONE[m.origen]}>{m.origen}</Badge>
                    </td>
                    <td className="px-3 py-2 text-neutral-700">
                      {m.motivo}
                      {m.observacion && (
                        <div className="text-xs text-neutral-500 italic">
                          {m.observacion}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">
                      {formatCLP(m.monto)}
                    </td>
                    <td className="px-3 py-2 text-neutral-600">
                      {m.fecha_vencimiento
                        ? formatDate(m.fecha_vencimiento)
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={ESTADO_TONE[m.estado]}>{m.estado}</Badge>
                    </td>
                    <td className="px-3 py-2 text-neutral-500 font-mono text-xs">
                      {m.lectura_id ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

interface FormProps {
  contratos: ContratoOperativo[];
  onClose: () => void;
}

function RegistrarMultaForm({ contratos, onClose }: FormProps): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [socioId, setSocioId] = useState<string>("");
  const [monto, setMonto] = useState<string>("");
  const [motivo, setMotivo] = useState("");
  const [origen, setOrigen] = useState<OrigenMulta>("Manual");
  const [vencimiento, setVencimiento] = useState("");
  const [observacion, setObservacion] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (p: MultaCreatePayload) => registrarMulta(p),
    onSuccess: (m) => {
      toast.success(`Multa #${m.id} registrada por ${formatCLP(m.monto)}`);
      void qc.invalidateQueries({ queryKey: ["multas"] });
      void qc.invalidateQueries({ queryKey: ["multas-socio", m.socio_id] });
      onClose();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo registrar";
      setError(msg);
      toast.error(msg);
    },
  });

  // Socios únicos (un socio puede tener varios contratos)
  const sociosUnicos = useMemo(() => {
    const seen = new Set<number>();
    const out: ContratoOperativo[] = [];
    for (const c of contratos) {
      if (!seen.has(c.socio_id)) {
        seen.add(c.socio_id);
        out.push(c);
      }
    }
    return out;
  }, [contratos]);

  function submit(): void {
    setError(null);
    if (!socioId) return setError("Elige un socio");
    const m = Number(monto);
    if (!Number.isFinite(m) || m <= 0) return setError("Monto inválido");
    if (motivo.trim().length < 3) return setError("Motivo requerido");
    mutation.mutate({
      socio_id: Number(socioId),
      monto: m,
      motivo: motivo.trim(),
      origen,
      fecha_vencimiento: vencimiento || null,
      observacion: observacion.trim() || null,
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([socioId, monto, motivo, origen, vencimiento, observacion]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mutation.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Registrar multa"
      icon={AlertTriangle}
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={intentarCerrar}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? "Guardando…" : "Registrar multa"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            label="Socio"
            value={socioId}
            onChange={(e) => setSocioId(e.target.value)}
            disabled={mutation.isPending}
          >
            <option value="">— elegir —</option>
            {sociosUnicos.map((c) => (
              <option key={c.socio_id} value={c.socio_id}>
                {c.socio_nombre}
              </option>
            ))}
          </Select>
          <Input
            label="Monto (CLP)"
            type="number"
            min={1}
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            disabled={mutation.isPending}
          />
          <Select
            label="Origen"
            value={origen}
            onChange={(e) => setOrigen(e.target.value as OrigenMulta)}
            disabled={mutation.isPending}
          >
            {ORIGENES.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
        </div>
        <Input
          label="Motivo"
          hint="Mínimo 3 caracteres"
          type="text"
          maxLength={255}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          disabled={mutation.isPending}
          placeholder="ej. Inasistencia a asamblea de marzo"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            label="Fecha de vencimiento (opcional)"
            type="date"
            value={vencimiento}
            onChange={(e) => setVencimiento(e.target.value)}
            disabled={mutation.isPending}
          />
          <Input
            label="Observación"
            type="text"
            value={observacion}
            onChange={(e) => setObservacion(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>

        {error && <p className="text-sm text-rose-700">{error}</p>}
        <p className="text-xs text-neutral-500">
          La multa se aplicará automáticamente a la próxima boleta del socio
          cuando se cree la lectura del próximo periodo.
        </p>
      </div>
      {dialogoDescarte}
    </Modal>
  );
}
